import { useCallback, useEffect, useState } from "react";
import { Shield, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const ROLE_STYLES = {
  admin: "bg-[#D4AF37]/15 text-[#8a6d1f]",
  staff: "bg-blue-50 text-blue-700",
  doctor: "bg-purple-50 text-purple-700",
};

// Team management — admin only. Staff/doctor accounts log into the same
// backoffice but see only the WhatsApp inbox (role-filtered tabs).
export const TeamTab = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success(`${form.role} account created for ${form.email}`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create user");
    }
  };

  const patch = async (id, body, note) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      toast.success(note);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="team-tab">
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#1A1615] to-[#4A4A4A]">
            <Users className="h-5 w-5 text-[#D4AF37]" />
          </span>
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">Team &amp; Roles</h2>
            <p className="text-xs font-light text-[#91837C]">
              Admin sees everything. Staff and doctors log into the same backoffice but only reach the WhatsApp inbox and their assigned conversations.
            </p>
          </div>
        </div>

        {/* create */}
        <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto_auto]">
          <input className="input-lux text-xs" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-new-name" />
          <input className="input-lux text-xs" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-new-email" />
          <input className="input-lux text-xs" type="password" placeholder="Password (8+ chars)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="team-new-password" />
          <select className="input-lux text-xs" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-new-role">
            <option value="staff">Staff</option>
            <option value="doctor">Doctor</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={create} data-testid="team-create" className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-[10px]">
            <UserPlus className="h-3.5 w-3.5" /> Add
          </button>
        </div>

        {/* list */}
        <ul className="mt-6 space-y-2" data-testid="team-list">
          {users.map((u) => (
            <li key={u._id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white/70 px-4 py-3" data-testid={`team-user-${u._id}`}>
              <Shield className="h-4 w-4 text-[#C9B8A8]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold text-[#1A1615]">{u.name || u.email}</span>
                <span className="block truncate text-[10px] font-light text-[#91837C]">{u.email}</span>
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${ROLE_STYLES[u.role] || ROLE_STYLES.staff}`}>{u.role}</span>
              {u.disabled && <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[9px] font-bold uppercase text-red-700">disabled</span>}
              <select
                value={u.role}
                onChange={(e) => patch(u._id, { role: e.target.value }, "Role updated")}
                data-testid={`team-role-${u._id}`}
                className="input-lux !w-auto text-[10px]"
              >
                <option value="staff">Staff</option>
                <option value="doctor">Doctor</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onClick={() => patch(u._id, { disabled: !u.disabled }, u.disabled ? "Account enabled" : "Account disabled")}
                data-testid={`team-disable-${u._id}`}
                className="btn-outline-lux px-3 py-1.5 text-[10px]"
              >
                {u.disabled ? "Enable" : "Disable"}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
